<template>
    <div class="card-header">
        <h3 class="card-title">{{ $t("brand_documents") }}</h3>
        <div class="card-toolbar">
            <button
                type="button"
                :disabled="buttondisabled"
                class="btn btn-sm btn-primary mr-1"
                @click="processImage"
            >
                {{ $t("upload") }}
            </button>
            <button
                type="reset"
                :disabled="buttondisabled"
                class="btn btn-sm btn-danger"
                @click="handleClickDeleteFile()"
            >
                {{ $t("remove") }}
            </button>
        </div>
    </div>

    <div class="card-body">
        <div class="form-group row">
            <div class="col-lg-12">
                <div v-if="state.files.length > 0" class="files">
                    <div
                        class="file-item"
                        v-for="(file, index) in state.files"
                        :key="index"
                    >
                        <!-- Show Preview if Image -->
                        <img
                            v-if="isImage(recentFile)"
                            :src="previewURL"
                            class="img-preview"
                        />

                        <!-- Show Preview for PDFs -->
                        <embed
                            v-if="isPDF(recentFile)"
                            :src="previewURL"
                            type="application/pdf"
                            width="100%"
                            height="200px"
                        />
                        <span>{{ file.name }}</span>
                        <!-- Show Download Link for Other Files -->
                        <!-- <p v-else><a :href="previewURL" target="_blank" class="text-primary">Download File</a></p> -->
                        <span
                            class="delete-file"
                            @click="handleClickDeleteFile(index)"
                            >{{ $t('delete') }}</span
                        >
                    </div>
                </div>
                <div
                    v-else
                    class="dropzone vue-dropzone"
                    v-bind="getRootProps()"
                >
                    <div
                        class="border"
                        :class="{
                            isDragActive,
                        }"
                    >
                        <input v-bind="getInputProps()" />
                        <div class="dropzone-custom-content">
                            <h3 class="dropzone-custom-title">
                                {{ $t("upload_content") }}
                            </h3>
                            <div class="subtitle">{{ $t("select_file") }}</div>
                            <div class="subtitle warning-text">
                                <i class="flaticon-warning"></i> {{ $t('file_size_limit_6mb_instruction') }}
                            </div>
                        </div>
                        <!-- <p v-if="isDragActive">Drop the files here ...</p>
                                <p v-else>Drag and drop files here, or Click to select files</p> -->
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, reactive, watch, onMounted, defineEmits } from "vue";
import CustomerAPIService from "@/js/api/customers/customers";
import { useDropzone } from "vue3-dropzone";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useStore } from "vuex";
import { defineProps } from "vue";

const customerId = ref("");
const buttondisabled = ref(true);
const recentFile = ref(null);
const previewURL = ref("");
const store = useStore();
const state = reactive({
    files: [],
});
const emits = defineEmits(["file-uploaded"]);

const { getRootProps, getInputProps, isDragActive, ...rest } = useDropzone({
    onDrop,
    multiple: false,
    accept: ".jpg,.png,.gif,pdf,.xlsx,.xls,.doc,.docx,.heic",
    maxSize: 6 * 1024 * 1024,
    maxFiles: 1,
    thumbnail: true,
});
const props = defineProps({
    filesList: Array,
    customerId: Number,
});

// Generate File Preview URL
const generatePreview = (file) => {
    if (file) {
        previewURL.value = URL.createObjectURL(file);
    }
};

// Check File Type
const isImage = (file) => file && file.type.startsWith("image/");
const isPDF = (file) => file && file.type === "application/pdf";
function onDrop(acceptFiles, rejectReasons) {
    // Reset previous rejection state
    state.files = [];
    if (rejectReasons.length > 0) {
        const firstRejectedFile = rejectReasons[0]?.file;
        if (firstRejectedFile) {
            const fileSizeInMB = bytesToMB(firstRejectedFile.size);
            toast.error(
                `You uploaded a ${fileSizeInMB} MB file, which exceeds the maximum allowed size of 6 MB.`
            );
        } else {
            toast.error("Invalid file uploaded.");
        }

        return; // Stop execution if the file is rejected
    }
    // If there are valid files, proceed with upload
    if (acceptFiles.length > 0) {
        buttondisabled.value = false; // Enable upload button
        recentFile.value = acceptFiles[0]; // Store the most recent file
        generatePreview(acceptFiles[0]); // Generate preview
        state.files = acceptFiles;
    }
}

function uploadFiles(files) {
    console.log("uploadFiles function");
    store.commit("SHOW_LOADER", true);
    const formData = new FormData();
    files.forEach((file) => {
        formData.append("file", file);
    });
    CustomerAPIService.customersFiles(formData, props.customerId)
        .then((response) => {
            store.commit("SHOW_LOADER", false);
            toast.success(response.data.success);
            uploadSuccess();
        })
        .catch((error) => {
            store.commit("SHOW_LOADER", false);
            toast.error(error);
            // Handle error
        });
}

function handleClickDeleteFile(index) {
    state.files.splice(index, 1);
    buttondisabled.value = true;
}

const bytesToMB = (bytes) => {
    return (bytes / (1024 * 1024)).toFixed(2); // Converts and rounds to 2 decimal places
};

const uploadSuccess = () => {
    state.files = [];
    console.log("uploadSuccess function");
    emits("file-uploaded");
    buttondisabled.value = true;
};

const processImage = () => {
    buttondisabled.value = false;
    console.log("processImage function");
    uploadFiles(state.files);
};
</script>
<style scoped>
.subtitle {
    color: #6c7293;
}
.dropzone,
.files {
    width: 100%;
    margin: 0 auto;
    padding: 10px;
    border-radius: 8px;
    box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px,
        rgba(60, 64, 67, 0.15) 0px 1px 3px 1px;
    font-size: 12px;
    line-height: 1.5;
}
.border {
    border: 2px dashed #ccc;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    transition: all 0.3s ease;
    background: #fff;
}

.file-item {
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgb(255 167 18 / 20%);
    padding: 7px;
    padding-left: 15px;
    margin-top: 10px;

    /* &:first-child {
    margin-top: 0;
  } */
}
.delete-file {
    background: red;
    color: #fff;
    padding: 5px 10px;
    border-radius: 8px;
    cursor: pointer;
}
.dropzone-custom-content {
    left: 50%;
    text-align: center;
}

.cursor-pointer {
    cursor: pointer;
}
.subtitle {
    color: #6c7293;
}
.vue-dropzone {
    border: dashed 2px #5d78ff;
}
.warning-text {
    color: rgb(255, 153, 0);
}
.flaticon-warning {
    color: rgb(255, 153, 0);
}
.img-preview {
    max-width: 200px;
    max-height: 200px;
    margin-top: 10px;
    border: 1px solid #ddd;
    padding: 5px;
}
</style>
